import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, CheckCircle, XCircle, User, Clock, RotateCcw, Camera, CameraOff } from "lucide-react";
import QRScanner from "../QRScanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable modal for individual ticket check-in.
 * Can be used in two modes:
 * - bookingId: show tickets for a specific booking
 * - eventId: show ALL tickets for an event (admin check-in station)
 */
export default function TicketCheckinModal({ open, onClose, bookingId, eventId, onUpdate }) {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [title, setTitle] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // QR Scanner state
  const [showQR, setShowQR] = useState(false);
  const [qrLocked, setQrLocked] = useState(false);
  const [lastScanResult, setLastScanResult] = useState(null); // {success, message, ticketId}
  const processingRef = useRef(false);

  const fetchTickets = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    const token = localStorage.getItem("auth_token");
    try {
      if (eventId) {
        const res = await axios.get(`${API}/events/${eventId}/tickets`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { search },
        });
        setTickets(res.data.tickets);
        setStats(res.data.stats);
        setTitle(res.data.event?.title || "Evento");
      } else if (bookingId) {
        const res = await axios.get(`${API}/bookings/${bookingId}/tickets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTickets(res.data.tickets.map(t => ({
          ticket_id: t.id,
          public_id: t.public_id,
          ticket_number: t.ticket_number,
          attendee_name: t.attendee_name,
          attendee_email: t.attendee_email,
          booking_name: res.data.booking?.name || "",
          status: t.status,
          checked_in_at: t.checked_in_at,
        })));
        setStats(res.data.stats);
        setTitle(`Pedido #${bookingId.slice(0, 8)} — ${res.data.booking?.name || ""}`);
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar ingressos");
    } finally {
      setLoading(false);
    }
  }, [open, eventId, bookingId, search]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleCheckin = async (publicId) => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.post(`${API}/tickets/${publicId}/checkin`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.info(res.data.message);
      }
      fetchTickets();
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro no check-in");
    }
  };

  const handleCancelCheckin = async (publicId) => {
    try {
      const token = localStorage.getItem("auth_token");
      await axios.post(`${API}/tickets/${publicId}/cancel-checkin`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Check-in cancelado");
      fetchTickets();
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cancelar check-in");
    }
  };

  // QR code scan handler — processes one scan at a time, prevents loops
  const handleQrScan = async (code) => {
    if (processingRef.current) return;
    processingRef.current = true;
    setQrLocked(true);

    // Extract public_id from the QR code (could be full URL or just the code)
    const publicId = code.includes("/") ? code.split("/").pop() : code;

    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.post(`${API}/tickets/${publicId}/checkin`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setLastScanResult({ success: true, message: `Check-in: ${res.data.attendee_name || publicId}` });
        toast.success(res.data.message || "Check-in realizado!");
        fetchTickets();
        onUpdate?.();
      } else {
        // Already checked in — info, not error
        setLastScanResult({ success: false, message: res.data.message || "Já realizado" });
        toast.info(res.data.message || "Check-in já realizado");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Ingresso não encontrado";
      setLastScanResult({ success: false, message: msg });
      toast.error(msg);
    }

    // Unlock scanner after delay to prevent immediate re-read of same QR
    setTimeout(() => {
      processingRef.current = false;
      setQrLocked(false);
      setLastScanResult(null);
    }, 2500);
  };

  // Reset QR state when modal closes
  useEffect(() => {
    if (!open) {
      setShowQR(false);
      setQrLocked(false);
      setLastScanResult(null);
      processingRef.current = false;
    }
  }, [open]);

  const checkedCount = stats.checked_in || 0;
  const totalCount = stats.total || 0;
  const pct = totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#fccf1f]">Check-in — {title}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Realize o check-in individual de cada ingresso
          </DialogDescription>
        </DialogHeader>

        {/* Stats bar */}
        <div className="flex items-center gap-4 bg-zinc-800/60 rounded-lg p-3">
          <div className="flex-1">
            <div className="flex justify-between text-xs text-zinc-400 mb-1">
              <span>{checkedCount} de {totalCount} ingressos</span>
              <span>{pct}%</span>
            </div>
            <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-emerald-400">{checkedCount}</p>
            <p className="text-[10px] text-zinc-500 uppercase">presentes</p>
          </div>
        </div>

        {/* QR Scanner Section */}
        {eventId && (
          <div className="space-y-2">
            <Button
              onClick={() => setShowQR(!showQR)}
              variant={showQR ? "outline" : "default"}
              className={showQR
                ? "w-full border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                : "w-full bg-[#fccf1f] hover:bg-[#e6bc1c] text-black font-medium"
              }
              data-testid="toggle-qr-scanner"
            >
              {showQR ? <><CameraOff className="w-4 h-4 mr-2" /> Fechar Câmera</> : <><Camera className="w-4 h-4 mr-2" /> Check-in por QR Code</>}
            </Button>

            {showQR && (
              <div className="relative">
                <QRScanner
                  onScan={handleQrScan}
                  isActive={showQR && open}
                  locked={qrLocked}
                />
                {/* Scan result overlay */}
                {lastScanResult && (
                  <div className={`mt-2 p-3 rounded-lg text-center text-sm font-medium ${
                    lastScanResult.success
                      ? "bg-emerald-500/20 border border-emerald-500/30 text-emerald-400"
                      : "bg-orange-500/20 border border-orange-500/30 text-orange-400"
                  }`}>
                    {lastScanResult.success ? <CheckCircle className="w-4 h-4 inline mr-2" /> : <XCircle className="w-4 h-4 inline mr-2" />}
                    {lastScanResult.message}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Search (only for event mode) */}
        {eventId && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, email ou código..."
              className="pl-10 bg-zinc-800 border-white/10 text-white placeholder:text-zinc-600 h-10"
              data-testid="checkin-search"
            />
          </div>
        )}

        {/* Tickets list */}
        {loading ? (
          <div className="flex justify-center py-8"><Clock className="w-6 h-6 animate-pulse text-zinc-500" /></div>
        ) : tickets.length === 0 ? (
          <p className="text-center text-zinc-500 py-8">Nenhum ingresso encontrado</p>
        ) : (
          <div className="space-y-1.5">
            {tickets.map((t) => (
              <div
                key={t.public_id}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                  t.status === "used"
                    ? "bg-emerald-500/10 border border-emerald-500/20"
                    : "bg-zinc-800/60 border border-white/5 hover:border-white/10"
                }`}
                data-testid={`checkin-ticket-${t.public_id}`}
              >
                {/* Status icon */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  t.status === "used" ? "bg-emerald-500/20" : "bg-zinc-700"
                }`}>
                  {t.status === "used"
                    ? <CheckCircle className="w-4 h-4 text-emerald-400" />
                    : <User className="w-4 h-4 text-zinc-400" />
                  }
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium truncate">{t.attendee_name}</p>
                  <p className="text-[10px] text-zinc-500 truncate">
                    {t.public_id} · Ingresso #{t.ticket_number}
                    {t.booking_name && t.booking_name !== t.attendee_name && ` · Pedido: ${t.booking_name}`}
                  </p>
                </div>

                {/* Check-in time */}
                {t.status === "used" && t.checked_in_at && (
                  <span className="text-[10px] text-emerald-400/70 flex-shrink-0">
                    {new Date(t.checked_in_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}

                {/* Action */}
                {t.status === "valid" ? (
                  <Button
                    size="sm"
                    onClick={() => handleCheckin(t.public_id)}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8 px-3 flex-shrink-0"
                    data-testid={`checkin-btn-${t.public_id}`}
                  >
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Check-in
                  </Button>
                ) : t.status === "used" ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleCancelCheckin(t.public_id)}
                    className="text-zinc-500 hover:text-red-400 text-xs h-8 px-2 flex-shrink-0"
                    data-testid={`cancel-checkin-btn-${t.public_id}`}
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Desfazer
                  </Button>
                ) : (
                  <span className="text-[10px] text-red-400 flex-shrink-0">
                    <XCircle className="w-3.5 h-3.5 inline mr-0.5" />Cancelado
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
